"use client";

import React, { ChangeEventHandler, Fragment, ReactNode, FC } from "react";

import { ClassNameMap } from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox, { CheckboxClassKey } from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import { useParams } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { FiltersMenuTranslations } from "~/bff/types/FiltersMenuTranslations";
import { SkuPrice } from "~/bff/types/SkuPrice";
import { FILTER_DIVIDER } from "~/components/products-layout/constants";
import { getCurrencyCodeByLocale } from "~/constants/i18n";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { Nullable } from "~/types/general.types";

import { AppliedFilters, HandleFiltersCallback } from "../../../../types";
import { getPriceFilterLabel } from "../../helpers";
import { FILTERS_CHECKBOX_SHARED_CLASSES } from "../filter-shared.constants";
import { MultiFilterContainer } from "../filter-shared.styled";

export interface PriceFilterProps {
  label: string;
  onClick?: HandleFiltersCallback;
  filters?: SkuPrice[];
  appliedFilters?: Nullable<AppliedFilters>;
  checkboxClasses?: Partial<ClassNameMap<CheckboxClassKey>>;
  checkedIcon?: ReactNode;
  translations?: Nullable<FiltersMenuTranslations>;
}

export const PriceFilter: FC<PriceFilterProps> = ({
  label,
  filters,
  onClick,
  appliedFilters,
  checkboxClasses,
  checkedIcon,
  translations,
}) => {
  const overTextTranslation = translations?.priceFilterOverText || "Over";
  const { locale } = useParams<GlobalPathParams>();
  return (
    <>
      {filters?.map(({ start, end, count }, index) => {
        const endNum = !end || isNaN(Number(end)) ? null : Number(end);
        const checkboxValue = JSON.stringify({
          start: start ?? 0,
          end: Number(endNum),
        });

        const checkboxLabel = getPriceFilterLabel(
          checkboxValue,
          locale,
          getCurrencyCodeByLocale(locale),
          overTextTranslation,
        );

        const filterObjectKey = `${label}${FILTER_DIVIDER}${checkboxLabel}${FILTER_DIVIDER}${checkboxValue}`;
        return (
          <Fragment key={`${checkboxLabel}_${index}`}>
            {!!count && (
              <MultiFilterContainer {...getTestAutomationProps("price-filter")}>
                <FormControlLabel
                  classes={{
                    root: FILTERS_CHECKBOX_SHARED_CLASSES.formControlRoot,
                    label: FILTERS_CHECKBOX_SHARED_CLASSES.formControlLabel,
                  }}
                  control={
                    <Checkbox
                      name={filterObjectKey}
                      value={count}
                      id={`${label}-${index}`}
                      onChange={onClick as ChangeEventHandler<HTMLInputElement>}
                      className={FILTERS_CHECKBOX_SHARED_CLASSES.checkBox}
                      checked={!!appliedFilters?.[filterObjectKey] || false}
                      classes={checkboxClasses}
                      checkedIcon={checkedIcon}
                      {...getTestAutomationProps("checkbox")}
                    />
                  }
                  label={
                    <Box className={FILTERS_CHECKBOX_SHARED_CLASSES.filterItemLabel}>
                      <Typography
                        variant="body2"
                        {...getTestAutomationProps("checkbox-label")}
                      >
                        {checkboxLabel}
                      </Typography>

                      <Typography
                        variant="body2"
                        {...getTestAutomationProps("count")}
                      >
                        {count}
                      </Typography>
                    </Box>
                  }
                />
              </MultiFilterContainer>
            )}
          </Fragment>
        );
      })}
    </>
  );
};
